<template>
  <d-container fluid class="main-content-container px-4">
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('marketing.salesTools') }}</span>
        <h3 class="page-title">{{ $t('marketing.editSegment') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/funkcje-sprzedazowe/segmenty')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>


        <d-button
          @click.prevent="exportCustomersList"
          :disabled="exportingCustomersList"
          theme="primary"
          class="ml-2"
          :title="$t('order.downloadOrderAttachment')">
          <template v-if="exportingCustomersList">
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </template>
          <template v-else>
            <i class="material-icons">cloud_download</i>
            {{ $t('marketing.exportClientsList') }}
          </template>
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
    </d-alert>

    <!-- Form -->
    <d-form
      v-else
      :class="{ 'is-loading': !loaded }">
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>

          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <d-row>
              <d-col cols="6">
                <div class="form-group">
                  <label for="name">
                    {{ $t('ui.name') }}:
                  </label>

                  <d-input
                    readonly
                    v-model="name" />
                </div>
              </d-col>

              <d-col cols="3">
                <div class="form-group">
                  <label for="customers_order_by">
                    {{ $t('marketing.customersOrderBy') }}:
                  </label>

                  <div class="form-control is-select">
                    <v-select
                      id="customers_order_by"
                      :readonly="true"
                      disabled
                      :options="customersOrderByOptions"
                      v-model="customers_order_by"
                      :clearable="false"
                      :searchable="false"
                      :reduce="item => item.value"
                      :placeholder="$t('ui.chooseOption')"
                      label="text">
                      <template slot="no-options">
                        {{ $t('ui.noOptionsAvailable') }}&hellip;
                      </template>
                    </v-select>
                  </div>
                </div>
              </d-col>

              <d-col cols="3">
                <div class="form-group">
                  <label for="customers_limit">
                    {{ $t('marketing.maxCustomers') }}:
                  </label>

                  <d-input
                    v-model="customers_limit"
                    class="form-control"
                    readonly
                    type="number"/>
                </div>
              </d-col>

              <d-col cols="12">
                <div class="form-group">
                  <label>
                    {{ $t('ui.description') }}:
                  </label>

                  <d-textarea
                    readonly
                    :value.prop="description"
                    v-model="description" />
                </div>
              </d-col>
            </d-row>
          </d-tab>

          <!-- Filtry -->
          <d-tab :title="$t('marketing.segmentFilters')">
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('marketing.segmentFilters') }}
            </h4>

              <div
                v-for="(filtersList, groupName) in groupedFilters"
                :key="groupName"
                class="pt-3 pl-3 pr-3 pb-2 bg-light rounded mb-3 border" >
                <h6 class="mt-0 mb-3 text-primary">
                  {{ getGroupName(groupName) }}
                </h6>

                <d-row>
                  <d-col
                    cols="12" md="6" lg="4"
                    v-for="filter in filtersList"
                    :key="filter.id"
                    class="mb-3" >
                    <segment-filter-readonly :filter="filter" />
                  </d-col>
                </d-row>
              </div>
          </d-tab>

          <d-tab :title="$t('customer.customersList')">
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('customer.customersList') }}
            </h4>
            <segment-results-preview
              v-if="loaded"
              :form-errors="{}"
              :segment-data="{ id: id }" />
          </d-tab>
        </d-tabs>

        <div v-if="!loaded && !loadError" class="pb-3 pt-3 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import SegmentFilterReadonly from './SegmentFilterReadonly.vue';
import SegmentResultsPreview from './SegmentResults.vue';

export default {
  name: 'marketing-segment-edit',
  components: {
    'segment-filter-readonly': SegmentFilterReadonly,
    'segment-results-preview': SegmentResultsPreview
  },
  computed: {
    groupedFilters() {
      if (!this.filters || !this.filters.length) return {};

      // Grupowanie filtrów po kluczu "group"
      return this.filters.reduce((acc, filter) => {
        if (!acc[filter.group]) {
          acc[filter.group] = [];
        }
        acc[filter.group].push(filter);
        return acc;
      }, {});
    }
  },
  data () {
    return {
      id: null,
      customers_limit: 1,
      customers_order_by: null,
      description: null,
      filters: [],
      name: '',
      loadError: false,
      loaded: false,
      exportingCustomersList: false,
      customersOrderByOptions: [
        {
          value: 'orders_count',
          text: this.$t('marketing.ordersCount')
        },
        {
          value: 'orders_amount',
          text: this.$t('marketing.ordersAmount')
        }
      ]
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/marketing/segments/item/',
      instanceFields: ['customers_limit', 'customers_order_by', 'description', 'filters', 'id', 'list_generated_at', 'name', 'updated_at'],
      loadedKey: 'loaded'
    });
  },
  methods: {
    getGroupName(groupCode) {
      const groups = {
        'customers': this.$t('customer.customers'),
        'orders': this.$t('customer.orders'),
        'products': this.$t('product.products')
      };
      return groups[groupCode] || groupCode;
    },
    exportCustomersList () {
      this.exportingCustomersList = true;

      this.$http.post(
        '/marketing/segments/customers/export',
        { segment_id: this.id },
        { responseType: 'blob' }
      ).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.exportingCustomersList = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.exportingCustomersList = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
